/**
 * @file schemas.ts
 * @brief Zod schemas + TS types for the Super Admin → Billing & Pricing
 *        feature surface.
 *
 * @details
 * The backend speaks JSON with `value` strings (TEXT columns) so we
 * coerce here at the boundary — every form field on the UI side stays
 * a real number / boolean. Three independent shapes:
 *
 *   1. SystemSettingsResponse — `{ settings: { [key]: { value, ... } } }`
 *      where `key` is one of the well-known constants seeded in
 *      `BACKEND/src/db/migrations/ensure_system_settings_table.js`
 *      (RATE_PER_MINUTE_*_MINOR, MIN_CHARGE_*_MINOR, TAX_RATE) plus
 *      the cost-alert ones from `cost-alert.service.js`
 *      (DAILY_COST_ALERT_THRESHOLD, COST_ALERT_EMAIL,
 *      COST_ALERT_ENABLED, LAST_COST_ALERT_DATE).
 *   2. PricingConfigResponse — internal OpenAI service rates keyed by
 *      `service_name` ({ cost_per_unit_cents, unit_type, updated_at }).
 *   3. CostStatusResponse — live "today vs threshold" pill the page
 *      uses on top of the cost-alert card.
 *
 * @dependencies
 * - zod
 *
 * @references
 * - BACKEND/src/features/admin/routes.js (GET/PUT /settings,
 *   GET/PUT /pricing-config, GET /cost-status,
 *   POST /check-cost-alert, POST /analytics/recompute-session-costs)
 * - BACKEND/src/features/billing/cost-alert.service.js
 *
 * @author DES
 * @date 26-05-2026
 */

import { z } from "zod";

// ----------------------------------------------------------------------
// System Settings
// Backend stores everything as TEXT in `value`; we coerce per-key at
// the form layer (CustomerRatesCard / CostAlertCard) because Zod
// doesn't know which keys are numbers vs strings vs booleans without
// the seed file as reference.
// ----------------------------------------------------------------------
export const SystemSettingEntrySchema = z.object({
  value: z.string(),
  description: z.string().nullable().optional(),
  value_type: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export const SystemSettingsResponseSchema = z.object({
  success: z.boolean().optional(),
  settings: z.record(z.string(), SystemSettingEntrySchema),
});

export type SystemSettingEntry = z.infer<typeof SystemSettingEntrySchema>;
export type SystemSettingsResponse = z.infer<
  typeof SystemSettingsResponseSchema
>;

// Well-known keys used by the Billing surface — kept as a const tuple
// so it's easy to filter the "everything else" view on the Settings
// page (Wave 4c) without hardcoding the same list twice.
export const BILLING_SETTING_KEYS = [
  // Interpretation (session_kind = 'standard')
  "RATE_PER_MINUTE_USD_MINOR",
  "RATE_PER_MINUTE_EUR_MINOR",
  "RATE_PER_MINUTE_GBP_MINOR",
  "MIN_CHARGE_USD_MINOR",
  "MIN_CHARGE_EUR_MINOR",
  "MIN_CHARGE_GBP_MINOR",
  // Realtime interpretation (session_kind = 'realtime')
  "RATE_REALTIME_PER_MINUTE_USD_MINOR",
  "RATE_REALTIME_PER_MINUTE_EUR_MINOR",
  "RATE_REALTIME_PER_MINUTE_GBP_MINOR",
  "MIN_CHARGE_REALTIME_USD_MINOR",
  "MIN_CHARGE_REALTIME_EUR_MINOR",
  "MIN_CHARGE_REALTIME_GBP_MINOR",
  // Meeting Intel (Boardroom) — per-minute + per-speaker isolation fee
  "RATE_BOARDROOM_PER_MINUTE_USD_MINOR",
  "RATE_BOARDROOM_PER_MINUTE_EUR_MINOR",
  "RATE_BOARDROOM_PER_MINUTE_GBP_MINOR",
  "BOARDROOM_SPEAKER_FEE_USD_MINOR",
  "BOARDROOM_SPEAKER_FEE_EUR_MINOR",
  "BOARDROOM_SPEAKER_FEE_GBP_MINOR",
  "MIN_CHARGE_BOARDROOM_USD_MINOR",
  "MIN_CHARGE_BOARDROOM_EUR_MINOR",
  "MIN_CHARGE_BOARDROOM_GBP_MINOR",
  // Personal Notes — single-stream; per-minute + min charge, no per-speaker fee
  "RATE_NOTE_PER_MINUTE_USD_MINOR",
  "RATE_NOTE_PER_MINUTE_EUR_MINOR",
  "RATE_NOTE_PER_MINUTE_GBP_MINOR",
  "MIN_CHARGE_NOTE_USD_MINOR",
  "MIN_CHARGE_NOTE_EUR_MINOR",
  "MIN_CHARGE_NOTE_GBP_MINOR",
  // Medical Assessment — fixed report fee (english_recording); bilingual uses Interpretation PPM
  "MEDICAL_REPORT_FIXED_USD_MINOR",
  "MEDICAL_REPORT_FIXED_EUR_MINOR",
  "MEDICAL_REPORT_FIXED_GBP_MINOR",
  // Platform-wide
  "TAX_RATE",
  "DAILY_COST_ALERT_THRESHOLD",
  "COST_ALERT_EMAIL",
  "COST_ALERT_ENABLED",
  "LAST_COST_ALERT_DATE",
  // ADR-019 subscriptions (25-07-2026). SUBSCRIPTION_COUNTRIES is no longer
  // read (22-10-2026): the block-list has its own route, see
  // BillingCountriesResponseSchema.
  "SUBSCRIPTION_ENABLED",
  "PLAN_STARTER_INCLUDED_MINUTES",
  "PLAN_PRO_INCLUDED_MINUTES",
  "PLAN_BUSINESS_INCLUDED_MINUTES",
  "PLAN_BUSINESS_SOFT_CAP_MINUTES",
  "PLAN_STARTER_PRICE_GBP_MINOR",
  "PLAN_PRO_PRICE_GBP_MINOR",
  "PLAN_BUSINESS_PRICE_GBP_MINOR",
  "PLAN_STARTER_PRICE_USD_MINOR",
  "PLAN_PRO_PRICE_USD_MINOR",
  "PLAN_BUSINESS_PRICE_USD_MINOR",
  "PLAN_STARTER_PRICE_EUR_MINOR",
  "PLAN_PRO_PRICE_EUR_MINOR",
  "PLAN_BUSINESS_PRICE_EUR_MINOR",
  "PLAN_STARTER_S2S_RATE_GBP_MINOR",
  "PLAN_PRO_S2S_RATE_GBP_MINOR",
  "PLAN_BUSINESS_S2S_RATE_GBP_MINOR",
  "PLAN_STARTER_REALTIME_RATE_GBP_MINOR",
  "PLAN_PRO_REALTIME_RATE_GBP_MINOR",
  "PLAN_BUSINESS_REALTIME_RATE_GBP_MINOR",
  "PLAN_STARTER_S2S_RATE_USD_MINOR",
  "PLAN_PRO_S2S_RATE_USD_MINOR",
  "PLAN_BUSINESS_S2S_RATE_USD_MINOR",
  "PLAN_STARTER_REALTIME_RATE_USD_MINOR",
  "PLAN_PRO_REALTIME_RATE_USD_MINOR",
  "PLAN_BUSINESS_REALTIME_RATE_USD_MINOR",
  "PLAN_STARTER_S2S_RATE_EUR_MINOR",
  "PLAN_PRO_S2S_RATE_EUR_MINOR",
  "PLAN_BUSINESS_S2S_RATE_EUR_MINOR",
  "PLAN_STARTER_REALTIME_RATE_EUR_MINOR",
  "PLAN_PRO_REALTIME_RATE_EUR_MINOR",
  "PLAN_BUSINESS_REALTIME_RATE_EUR_MINOR",
  // ADR-019 — per-plan MI speaker fee (Starter / Pro / Business)
  "PLAN_STARTER_SPEAKER_FEE_GBP_MINOR",
  "PLAN_PRO_SPEAKER_FEE_GBP_MINOR",
  "PLAN_BUSINESS_SPEAKER_FEE_GBP_MINOR",
  "PLAN_STARTER_SPEAKER_FEE_USD_MINOR",
  "PLAN_PRO_SPEAKER_FEE_USD_MINOR",
  "PLAN_BUSINESS_SPEAKER_FEE_USD_MINOR",
  "PLAN_STARTER_SPEAKER_FEE_EUR_MINOR",
  "PLAN_PRO_SPEAKER_FEE_EUR_MINOR",
  "PLAN_BUSINESS_SPEAKER_FEE_EUR_MINOR",
  // Meeting search allowances (03-08-2026). Searches are counted per SEAT, not
  // per minute — search cost scales with people, not with billable time, so a
  // seat that records little but searches a shared corpus heavily is the case
  // these bound. Enforcement stays off until real usage justifies the numbers.
  // Related: BACKEND/src/db/migrations/ensure_meeting_search.js
  "SEARCH_ENABLED",
  "SEARCH_ENFORCE_LIMITS",
  "PLAN_BASIC_SEARCH_LIMIT",
  "PLAN_STARTER_SEARCH_LIMIT",
  "PLAN_PRO_SEARCH_LIMIT",
  "PLAN_BUSINESS_SEARCH_LIMIT",
  "PLAN_ENTERPRISE_SEARCH_LIMIT_PER_SEAT",
] as const;
export type BillingSettingKey = (typeof BILLING_SETTING_KEYS)[number];

// ----------------------------------------------------------------------
// Pricing Config (OpenAI service rates)
// ----------------------------------------------------------------------
// Super Admin billing countries = block-list (BACKEND B1, 22-10-2026).
// GET/PUT /api/admin/billing-countries. `saved: false` = no row yet, the
// defaults (EU 27 + EEA + sanctions) are in force.
export const BillingCountrySchema = z.object({
  code: z.string(),
  name: z.string(),
});
export type BillingCountry = z.infer<typeof BillingCountrySchema>;

export const BillingCountriesResponseSchema = z.object({
  success: z.boolean().optional(),
  blocked: z.array(BillingCountrySchema),
  saved: z.boolean(),
  defaults: z.array(BillingCountrySchema).default([]),
});
export type BillingCountriesResponse = z.infer<
  typeof BillingCountriesResponseSchema
>;

// GET /api/admin/plans (BACKEND B3, 22-10-2026): the four launch plans as
// cards. Read-only; edits go through PUT /api/admin/settings with these keys.
// B20 (24-10-2026): `choice` (FREE_PLAN_RESET_MODE, with `options`) and
// `seconds` (voice-note length) kinds, a string `effective` for a choice, and
// `rule` { appliesFrom, affects } per setting. A kind this build doesn't know
// parses as "other" and shows read-only, so a newer backend never blanks the
// page (before W17 an unknown kind failed the whole parse).
const KNOWN_PLAN_KINDS = ["minor", "minutes", "count", "limit", "days", "seats", "switch", "choice", "seconds"] as const;

export const PlanSettingRuleSchema = z.object({
  appliesFrom: z.string(),
  affects: z.string(),
});
export type PlanSettingRule = z.infer<typeof PlanSettingRuleSchema>;

export const PlanSettingSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.union([z.enum(KNOWN_PLAN_KINDS), z.string().transform(() => "other" as const)]),
  currency: z.enum(["GBP", "USD"]).optional(),
  interval: z.enum(["month", "year"]).optional(),
  value: z.string().nullable(),
  effective: z.union([z.number(), z.boolean(), z.string()]).nullable(),
  options: z.array(z.string()).optional(),
  rule: PlanSettingRuleSchema.nullable().optional(),
});

export const PlanRatesSchema = z.object({
  interpretationMinor: z.number(),
  realtimeMinor: z.number(),
  extraIntelligenceMinor: z.number(),
  minChargeMinor: z.number().nullable(),
  // BACKEND W18 (24-10-2026): the interpretation minimum per session, the
  // same platform value on every plan. Optional until that deploy.
  interpretationMinMinor: z.number().optional(),
  realtimeMinMinor: z.number().optional(),
  // BACKEND B23 (24-10-2026): pay-as-you-go Meeting Notes (no plan):
  // RATE_NOTE_PER_MINUTE_<CUR>_MINOR and MIN_CHARGE_NOTE_<CUR>_MINOR.
  noteMinor: z.number().optional(),
  noteMinChargeMinor: z.number().optional(),
});

// BACKEND W18: what a Free / Pro / Business card includes beyond its
// settings. null = unlimited (history) or not on this plan (Notes minutes /
// fair use). Optional until that deploy; the page shows "–" without it.
export const PlanIncludedSchema = z.object({
  notesMinutes: z.number().nullable(),
  notesFairUseMinutes: z.number().nullable(),
  notesPoolOn: z.boolean(),
  historyDays: z.number().nullable(),
  interpretationMinutes: z.number(),
  interpretationOnce: z.boolean(),
  // BACKEND B20 (24-10-2026). Free: resetMode "monthly" | "one_time",
  // expiryDays (null = never), askLimit. Every card: maxSeats (null = none).
  resetMode: z.string().nullable().optional(),
  expiryDays: z.number().nullable().optional(),
  askLimit: z.number().nullable().optional(),
  maxSeats: z.number().nullable().optional(),
});

export const PlanCardSchema = z.object({
  code: z.enum(["free", "pro", "business", "enterprise"]),
  name: z.string(),
  enabled: z.boolean(),
  contactSales: z.boolean().optional(),
  settings: z.array(PlanSettingSchema),
  rates: z.record(z.string(), PlanRatesSchema).nullable(),
  included: PlanIncludedSchema.optional(),
});

export const AdminPlansResponseSchema = z.object({
  success: z.boolean().optional(),
  currencies: z.array(z.string()),
  plans: z.array(PlanCardSchema),
  shared: z.object({
    settings: z.array(PlanSettingSchema),
    noPlan: z.record(z.string(), PlanRatesSchema),
  }),
});
export type PlanCard = z.infer<typeof PlanCardSchema>;
export type AdminPlansResponse = z.infer<typeof AdminPlansResponseSchema>;

// GET /api/admin/plans/impact?key=&value= (BACKEND B20): who a change
// reaches, counted before Save. A count is null when the server couldn't
// count it (a table not there yet): show "–", never a guess.
export const PlanImpactSchema = z
  .object({
    success: z.boolean().optional(),
    key: z.string(),
    current: z.string().nullable().optional(),
    proposed: z.string().nullable().optional(),
    valid: z.boolean().optional(),
    direction: z.string().optional(),
    rule: PlanSettingRuleSchema.nullable().optional(),
    counts: z.record(z.string(), z.number().nullable()).default({}),
    note: z.string().nullable().optional(),
  })
  .passthrough();
export type PlanImpact = z.infer<typeof PlanImpactSchema>;

export const PricingEntrySchema = z.object({
  cost_per_unit_cents: z.coerce.number(),
  unit_type: z.string(),
  updated_at: z.string().nullable().optional(),
});

export const PricingConfigResponseSchema = z.object({
  success: z.boolean().optional(),
  pricing: z.record(z.string(), PricingEntrySchema),
});

export type PricingEntry = z.infer<typeof PricingEntrySchema>;
export type PricingConfigResponse = z.infer<typeof PricingConfigResponseSchema>;

// ----------------------------------------------------------------------
// Cost Status — `/api/admin/cost-status`
// Returned shape: { enabled, threshold, current_cost, percent_used,
//                   is_warning, is_critical }
// All money fields are in cents (minor units).
// ----------------------------------------------------------------------
export const CostStatusSchema = z.object({
  enabled: z.boolean(),
  threshold: z.coerce.number(),
  current_cost: z.coerce.number(),
  percent_used: z.coerce.number(),
  is_warning: z.boolean(),
  is_critical: z.boolean(),
});

export type CostStatus = z.infer<typeof CostStatusSchema>;

// ----------------------------------------------------------------------
// Recompute Costs — request + response
// ----------------------------------------------------------------------
export const RecomputeRequestSchema = z.object({
  startDate: z.string().min(1),
  endDate: z.string().min(1),
});

export const RecomputeResponseSchema = z.object({
  success: z.boolean(),
  updated: z.coerce.number(),
});

export type RecomputeRequest = z.infer<typeof RecomputeRequestSchema>;
export type RecomputeResponse = z.infer<typeof RecomputeResponseSchema>;

// ----------------------------------------------------------------------
// Manual cost-alert trigger — `/api/admin/check-cost-alert`
// Response is { alerted: boolean, reason?: string, ... }. We keep the
// shape loose since the service returns several flavours of "skip".
// ----------------------------------------------------------------------
// POST /api/admin/cost-alert/test (06-10-2026): one real test email to the
// configured recipient, regardless of threshold or the enabled switch.
export const CostAlertTestResponseSchema = z
  .object({
    sent: z.boolean(),
    email: z.string().optional(),
    reason: z.string().optional(),
    cost: z.coerce.number().optional(),
    threshold: z.coerce.number().optional(),
  })
  .passthrough();

export type CostAlertTestResponse = z.infer<typeof CostAlertTestResponseSchema>;
